import ConfirmationWrapper from '../../Components/ConfirmationWrapper';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import {useGetTravelOptionsQuery} from '../../../../../api/services/lookupsService';
import {useGetTravelModeQuery, useUpdateTravelModeMutation} from '../../../../../api/services/studentService';
import {useParams} from 'react-router-dom';
import React, {useEffect, useState} from 'react';
import {Box, Card, Checkbox, Typography} from '@mui/material';
import PropTypes from 'prop-types';

const TravelMode = (
    {
        readonly,
        profile,
        school,
        onConfirmSuccess
    }) => {
    const {studentId} = useParams();
    const {
        data: travelOptions = [],
        isLoading: isTravelOptionsLoading
    } = useGetTravelOptionsQuery(undefined);
    const {
        data: travelModeData = {},
        isLoading: isTravelsLoading,
        isFetching: isTravelsFetching,
        refetch: refetchTravelModeData
    } = useGetTravelModeQuery(studentId);
    const [updateTravelMode] = useUpdateTravelModeMutation();
    const [travelModeId, setTravelModeId] = useState(0);
    const isLoading = isTravelsLoading ||
        isTravelOptionsLoading ||
        isTravelsFetching;

    useEffect(() => {
        if (travelModeData?.modeId) {
            setTravelModeId(travelModeData.modeId);
        }
    }, [travelModeData]);

    const handleSave = async () =>
        await updateTravelMode({
            studentId,
            body: {
                modeId: travelModeId === 0 ? -1 : travelModeId,
                studentId
            }
        });

    return (
        isLoading
            ? ''
            : <ConfirmationWrapper
                readonly={readonly}
                confirmData={{
                    confirmMessage: `I confirm that the information in the Mode of Travel section of ${profile.name} ${profile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${profile.name} ${profile.surname} in their care on behalf of ${school.name}`,
                    confirmedAt: travelModeData.confirmedAt,
                    confirmedBy: travelModeData.confirmedBy,
                    enableSaveButton: travelModeId > 0 && travelModeId !== travelModeData?.modeId,
                    enableConfirmButton: travelModeId > 0,
                    studentId,
                    type: STUDENT_PROFILE_CONFIRMATION_TYPES.TravelMode,
                    handleSave,
                    onConfirmSuccess: async () => {
                        await onConfirmSuccess();
                        await refetchTravelModeData();
                    },
                    confirmButtonText: 'Confirm Travel Mode'
                }}
                // Maria, 18 October 2026: the line above the choices, the choices as ticks in columns, and the
                // confirm box below the section
                actionsBelow
                heading={(
                    <Typography data-testid="travel-mode-question">
                        {profile.isApplicant
                            ? `Please let us know how ${profile.name} will usually travel to ${school.name}.`
                            : `Please let us know how ${profile.name} usually travels to school.`}
                    </Typography>
                )}
            >
                <Card variant="outlined" sx={{mt: 1, borderRadius: 2, display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))'}}}>
                    {travelOptions.map(({description, id}) => (
                        <Box
                            component="label"
                            key={id}
                            data-testid="travel-mode-option"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 2,
                                py: 1,
                                cursor: readonly ? 'default' : 'pointer',
                                backgroundColor: travelModeId === id ? '#eaf2fb' : 'transparent',
                                boxShadow: '1px 0 0 #eef0f3, 0 1px 0 #eef0f3'
                            }}
                        >
                            {/* One choice only: ticking a box unticks the one ticked before */}
                            <Checkbox
                                sx={{p: 0}}
                                checked={travelModeId === id}
                                disabled={readonly}
                                onChange={e => setTravelModeId(e.target.checked ? id : 0)}
                                inputProps={{'aria-label': description}}
                            />
                            <Typography fontWeight={travelModeId === id ? 600 : 400}>{description}</Typography>
                        </Box>
                    ))}
                </Card>
            </ConfirmationWrapper>
    );
};

TravelMode.propTypes = {
    readonly: PropTypes.bool,
    profile: PropTypes.object.isRequired,
    school: PropTypes.object.isRequired,
    onDataLoading: PropTypes.func,
    onConfirmSuccess: PropTypes.func,
};

export default TravelMode;